import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_SECONDS = 300;
const TYPES = [
  ['audio/webm;codecs=opus', 'webm'],
  ['audio/webm', 'webm'],
  ['audio/ogg;codecs=opus', 'ogg'],
  ['audio/mp4', 'm4a'],
];

export const canRecordVoice = () =>
  typeof window !== 'undefined' &&
  !!window.MediaRecorder &&
  !!navigator.mediaDevices?.getUserMedia &&
  window.isSecureContext !== false;

/**
 * ضبط پیام صوتی با MediaRecorder. stop(true) فایل و مدت را به onRecorded می‌دهد؛ stop(false) لغو می‌کند.
 */
const useVoiceRecorder = ({ onRecorded, onError }) => {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const stateRef = useRef(null);
  const callbacks = useRef({ onRecorded, onError });
  callbacks.current = { onRecorded, onError };

  const cleanup = () => {
    const st = stateRef.current;
    if (!st) return;
    clearInterval(st.timer);
    st.stream.getTracks().forEach((t) => t.stop());
    stateRef.current = null;
    setRecording(false);
  };

  const stop = useCallback((send) => {
    const st = stateRef.current;
    if (!st) return;
    st.send = send;
    try {
      st.recorder.stop();
    } catch {
      cleanup();
    }
  }, []);

  const start = useCallback(async () => {
    if (stateRef.current || !canRecordVoice()) return;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      callbacks.current.onError?.('دسترسی به میکروفون داده نشد');
      return;
    }
    const type = TYPES.find(([mime]) => !MediaRecorder.isTypeSupported || MediaRecorder.isTypeSupported(mime)) || ['', 'webm'];
    let recorder;
    try {
      recorder = type[0] ? new MediaRecorder(stream, { mimeType: type[0], audioBitsPerSecond: 32000 }) : new MediaRecorder(stream);
    } catch {
      recorder = new MediaRecorder(stream);
    }
    const chunks = [];
    const startedAt = Date.now();
    recorder.ondataavailable = (e) => {
      if (e.data?.size) chunks.push(e.data);
    };
    recorder.onstop = () => {
      const st = stateRef.current;
      const elapsed = (Date.now() - startedAt) / 1000;
      cleanup();
      if (!st?.send || !chunks.length) return;
      if (elapsed < 1) {
        callbacks.current.onError?.('پیام صوتی خیلی کوتاه است');
        return;
      }
      const mime = (recorder.mimeType || 'audio/webm').split(';')[0];
      const file = new File(chunks, `voice-${Date.now()}.${type[1]}`, { type: mime });
      callbacks.current.onRecorded?.(file, Math.round(elapsed * 10) / 10);
    };
    const timer = setInterval(() => {
      const sec = (Date.now() - startedAt) / 1000;
      setSeconds(sec);
      if (sec >= MAX_SECONDS) stop(true);
    }, 250);
    stateRef.current = { recorder, stream, timer, send: false };
    recorder.start(250);
    setSeconds(0);
    setRecording(true);
  }, [stop]);

  useEffect(() => () => stop(false), [stop]);

  return { recording, seconds, start, stop };
};

export default useVoiceRecorder;
